import {
  useMediaQuery,
  WhmButton,
  WhmSkeleton,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { CurrencyFormat } from "@securitize/reactjs-sid-utils";
import { ReactComponent as ExternalLinkIcon } from "@securitize/assets-sec-ui/dist/icons/svg/external-link.svg";
import { useGetFundBalances } from "../../../../../../data/usePortfolio";
import config from "../../../../../../data/config";
import { translationKeys } from "../../../../../../contexts/I18nContext";
import { Translate, TranslateHtml } from "../../../../../Translation/Translate";
import { ErrorPage } from "../../../../../shared/ErrorPage";
import StyledCard from "../../../StyledCard";
import SfsInvestorCard from "./SfsInvestorCard";

const usdFormat = new CurrencyFormat({ currency: "USD" });

function SfsAssetsSkeleton() {
  return (
    <WhmStack gap="16px">
      <WhmSkeleton variant="rounded" height={80} />
      {new Array(3).fill(0).map((_, index) => (
        <WhmSkeleton key={`skeleton-${index}`} variant="rounded" height={72} />
      ))}
    </WhmStack>
  );
}

export default function SfsAssets() {
  const fundBalances = useGetFundBalances();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  if (fundBalances.isLoading) {
    return <SfsAssetsSkeleton />;
  }

  if (fundBalances.isError) {
    return (
      <ErrorPage
        errorCode={fundBalances.error.correlationId}
        onRefresh={() => fundBalances.refetch()}
      />
    );
  }

  if (!fundBalances.data) {
    return null;
  }

  return (
    <WhmStack gap="16px">
      <WhmStack
        direction={isMobile ? "column" : "row"}
        gap="16px"
        sx={{ alignItems: isMobile ? "stretch" : "center", px: 2 }}
      >
        <WhmTypography sx={{ flex: 1 }}>
          <Translate
            translationKey={translationKeys.PORTFOLIO_SFS_DESCRIPTION}
          />
        </WhmTypography>
        <WhmButton
          variant="outlined"
          onClick={() =>
            window.open(config.SFS_PORTAL_URL, "_blank", "noopener,noreferrer")
          }
          endIcon={<ExternalLinkIcon fill="currentColor" />}
          sx={{ whiteSpace: "nowrap" }}
        >
          <Translate
            translationKey={translationKeys.PORTFOLIO_SFS_PORTAL_BUTTON}
          />
        </WhmButton>
      </WhmStack>

      <StyledCard>
        <WhmStack
          direction="row"
          gap="16px"
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <WhmStack>
            <WhmTypography variant="h6">
              <Translate
                translationKey={translationKeys.PORTFOLIO_SFS_TOTAL_TITLE}
              />
            </WhmTypography>
            <WhmTypography variant="body2" color="text.secondary">
              <Translate
                translationKey={translationKeys.PORTFOLIO_SFS_TOTAL_SUBTITLE}
              />
            </WhmTypography>
          </WhmStack>
          <WhmTypography variant="h6" sx={{ whiteSpace: "nowrap" }}>
            {usdFormat.format(fundBalances.data.usdTotalBalance)}
            <sup>*</sup>
          </WhmTypography>
        </WhmStack>
      </StyledCard>

      {fundBalances.data.investors.map((investor, index) => (
        <SfsInvestorCard
          key={`${investor.investorName}-${index}`}
          {...investor}
        />
      ))}

      <WhmTypography variant="body2" color="text.secondary">
        <TranslateHtml
          translationKey={translationKeys.PORTFOLIO_SFS_DISCLAIMER}
        />
      </WhmTypography>
    </WhmStack>
  );
}
